/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React, { useEffect, useMemo, useRef } from 'react'
import req from '@sportninja/common/api/request'
import { t } from '@sportninja/common/i18n'
import parsers from '@sportninja/common/reducers/parsers'
import { getAllOfficialTypes } from '@sportninja/common/selectors/types'
import { connect } from 'react-redux'
import colors from '@sportninja/common/constants/appColors'
import { Table, Input, Spin } from 'antd'
import { SearchOutlined } from '@ant-design/icons'

import { officialForm } from '../Form/form-configs'
import { InviteAction } from '../ActionPanel/ActionPanelActions'
import OptionButtonEdit from './OptionButtons/Edit'
import OptionButtonDelete from './OptionButtons/Delete'
import PersonCell from './PersonCell'
import MoreOptions from './OptionButtons/More'
import useOfficialList from './useOfficialList'
import useWindowSize from 'src/hooks/useWindowSize'
import { paragraphMedium, weightRegular } from '../css'
import { getTableStyles } from 'src/styles/tableStyles'
import { formatPhoneNumber } from 'react-phone-number-input'

const SCROLL_THRESHOLD = 500

const OfficialListTable = ({
  entityType,
  entityId,
  slug,
  refreshKey,
  isSoccer = false,
  isOrg = false,
  sportId,
  officialTypes,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 1024, [width])

  const { items, loading, hasMore, search, handleSearch, loadMore, refresh } =
    useOfficialList(slug)

  // Ref to prevent multiple simultaneous loadMore calls
  const isLoadingMoreRef = useRef(false)
  // Ref to track initial mount and prevent refresh on first render
  const isInitialMountRef = useRef(true)

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      return
    }

    if (refreshKey !== undefined) {
      refresh()
    }
  }, [refreshKey, refresh])

  const handleScroll = React.useCallback(() => {
    if (loading || isLoadingMoreRef.current) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = document.documentElement
    const nearBottom =
      scrollHeight - (scrollTop + clientHeight) < SCROLL_THRESHOLD
    if (nearBottom && hasMore) {
      isLoadingMoreRef.current = true
      loadMore()
    }
  }, [hasMore, loading, loadMore])

  useEffect(() => {
    if (!loading) {
      isLoadingMoreRef.current = false
    }
  }, [loading])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  const columns = useMemo(() => {
    const cols = [
      {
        title: t('common:name'),
        key: 'name_first',
        dataIndex: 'name_first',
        width: 300,
        render: (_, item) => <PersonCell item={item} />,
      },
      {
        title: 'Role',
        key: 'official_type_id',
        dataIndex: 'official_type',
        render: (officialTypeId) => {
          return (
            officialTypes.find((t) => t.id === officialTypeId)?.name_full ||
            '--'
          )
        },
      },
      {
        title: t('common:email'),
        key: 'email',
        dataIndex: 'email',
        render: (email) => email || '--',
      },
      {
        title: t('common:phone'),
        key: 'phone_number',
        dataIndex: 'phone_number',
        render: (phoneNumber) => formatPhoneNumber(phoneNumber) || '',
      },
      {
        title: 'Invite Status',
        key: 'invite_status',
        render: (_, item) => {
          if (item?.is_invited) {
            return 'Invited'
          }
          if (item?.is_accepted) {
            return 'Accepted'
          }
          return '-'
        },
      },
      {
        // title: 'Options',
        title: '',
        key: 'actions',
        align: 'right',
        width: 150,
        render: (_, item) => {
          const itemSlug = `/${entityType}/${entityId}/officials/${item.id}`

          const onEdit = async (...args) => {
            const [values] = args
            if (values?.phone_number) {
              if (!values?.phone_number_country) {
                values.phone_number_country = 'US'
              }
            }
            const body = JSON.stringify(values)
            const response = await req(itemSlug, {
              method: 'PUT',
              body,
            })
            await refresh()
            return response
          }

          return (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                justify-content: flex-end;
              `}
            >
              <OptionButtonEdit
                form={officialForm(
                  parsers.official(item),
                  isSoccer,
                  isOrg,
                  sportId
                )}
                onSubmit={onEdit}
                title={t('Web:editOfficial')}
              />
              <InviteAction
                invite={{
                  is_invited: item.is_invited,
                  is_accepted: item.is_accepted,
                  invitation_id: item.invitation_id,
                }}
                user={{
                  id: item.id,
                  name_first: item.name_first,
                  name_last: item.name_last,
                  official_type_id: item.official_type,
                  email: item?.email,
                }}
                slug={itemSlug}
                onComplete={refresh}
              />
              <MoreOptions
                options={[
                  {
                    key: 'remove',
                    Component: OptionButtonDelete,
                    componentProps: {
                      buttonText: 'remove',
                      slug: itemSlug,
                      itemId: item.id,
                      iconName: 'user-minus',
                      title: `Remove ${item.full_name}`,
                      type: t('common:official'),
                      onComplete: refresh,
                    },
                  },
                ]}
              />
            </div>
          )
        },
      },
    ]

    return cols
  }, [entityType, entityId, refresh, officialTypes, isSoccer, isOrg, sportId])

  return (
    <div
      css={css`
        width: 100%;
        margin-top: 24px;
      `}
    >
      <div
        css={css`
          margin-top: 16px;
          margin-bottom: 16px;
          display: none;
          justify-content: flex-start;
          flex-wrap: wrap;
          gap: 16px;
          @media (max-width: 768px) {
            margin-left: 16px;
            flex-direction: column;
          }
        `}
      >
        <Input
          className='list-search-input'
          placeholder={t('common:search')}
          prefix={
            <SearchOutlined
              css={css`
                font-size: 16px;
                color: ${colors.NEUTRAL_50};
              `}
            />
          }
          suffix={loading && items.length > 0 ? <Spin size='small' /> : null}
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear={true}
          css={css`
            ${paragraphMedium}
            ${weightRegular}
            width: 300px;
            color: ${colors.WHITE};
            border-radius: 8px;
            background: transparent;
            height: 40px !important;
            .ant-input {
              background-color: transparent;
              color: ${colors.WHITE};
            }
            .ant-input-prefix {
              color: ${colors.NEUTRAL_100};
            }
            &::placeholder {
              color: ${colors.NEUTRAL_100};
            }
            @media (max-width: 768px) {
              width: 90vw;
            }
            &:hover {
              background-color: transparent;
            }
          `}
        />
      </div>
      {loading && items.length === 0 ? (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 200px;
          `}
        >
          <Spin
            size='small'
            css={css`
              .ant-spin-dot-item {
                background-color: ${colors.PRIMARY};
              }
            `}
          />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={items}
          rowKey={(item) => item.id}
          pagination={false}
          onChange={(pagination, filters, sorter) => {
            // Sorting removed
          }}
          size='small'
          scroll={{ x: isMobile || width < 1200 ? 1000 : undefined }}
          css={getTableStyles(isMobile)}
        />
      )}
      {loading && items.length > 0 && (
        <div
          css={css`
            display: flex;
            justify-content: center;
            padding: 20px;
          `}
        >
          <Spin size='small' color={colors.PRIMARY} />
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    officialTypes: getAllOfficialTypes(state),
  }
}

export default connect(mapStateToProps)(OfficialListTable)
